import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, FormControlLabel, InputAdornment, Skeleton, Switch, TextField, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { C, cardSx, tabular } from '../RestaurantDashboard/tokens'
import { cardSubSx, cardTitleSx } from '../RestaurantDashboard/SalesOverTime'
import { $, count, pct, rate } from './saUtil'

/* The restaurant Dashboard picks this up on open, so it shows the same range. */
export const RANGE_HANDOFF_KEY = 'rmf.dashRangeHandoff'

const COLS = [
  { key: 'name', label: 'Restaurant', align: 'left', text: true },
  { key: 'orders', label: 'Orders', fmt: count },
  { key: 'itemSubtotal', label: 'Item subtotal', fmt: $ },
  { key: 'discounts', label: 'Discounts', fmt: $ },
  { key: 'deliveryFees', label: 'Delivery fees', fmt: $ },
  { key: 'tips', label: 'Tips', fmt: $ },
  { key: 'tax', label: 'Tax', fmt: $ },
  { key: 'grandTotal', label: 'Grand total', fmt: $, bar: true },
  { key: 'commissionRate', label: 'Commission rate', fmt: rate },
  { key: 'commission', label: 'Commission $', fmt: $ },
  { key: 'refunds', label: 'Refunds', fmt: $ },
  { key: 'payoutOwed', label: 'Payout owed', fmt: $ },
  { key: 'gmvDelta', label: 'Δ GMV', fmt: null }
]

const gmvDelta = r => (r.prevGrandTotal == null ? null : r.prevGrandTotal === 0 ? (r.grandTotal > 0 ? Infinity : 0) : (r.grandTotal - r.prevGrandTotal) / Math.abs(r.prevGrandTotal))

function DeltaCell({ row }) {
  const d = gmvDelta(row)
  if (d == null) return <Box component="span" sx={{ color: C.textMuted }}>—</Box>
  if (d === Infinity) return <Box component="span" sx={{ color: C.upText, fontWeight: 600 }}>New</Box>
  if (Math.abs(d) < 0.0005) return <Box component="span" sx={{ color: C.textMuted }}>0%</Box>
  return <Box component="span" sx={{ color: d > 0 ? C.upText : C.downText, fontWeight: 600 }}>{d > 0 ? '▲' : '▼'} {pct(d * 100)}</Box>
}

const cellBase = { px: 1.5, py: 1.1, whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }

export default function Leaderboard({ data, loading, range }) {
  const history = useHistory()
  const [sort, setSort] = useState({ key: 'grandTotal', dir: -1 })
  const [q, setQ] = useState('')
  const [showZero, setShowZero] = useState(false)

  const rows = useMemo(() => {
    const list = (data?.rows || [])
      .filter(r => showZero || r.orders > 0)
      .filter(r => !q || r.name.toLowerCase().includes(q.trim().toLowerCase()))
    const val = r => (sort.key === 'gmvDelta' ? gmvDelta(r) : r[sort.key])
    return list.slice().sort((a, b) => {
      const x = val(a)
      const y = val(b)
      if (sort.key === 'name') return String(x).localeCompare(String(y)) * sort.dir
      if (x == null && y == null) return 0
      if (x == null) return 1
      if (y == null) return -1
      return (x - y) * sort.dir
    })
  }, [data, sort, q, showZero])

  const t = data?.totals
  const open = r => {
    try {
      localStorage.setItem('restaurantId', r.restaurantId)
      localStorage.setItem('restaurantName', r.name)
      localStorage.removeItem('restaurantImage')
      if (range && range.from && range.to) sessionStorage.setItem(RANGE_HANDOFF_KEY, JSON.stringify({ from: range.from, to: range.to, compare: range.compare }))
    } catch (_) {}
    history.push(`/admin/dashboard/${r.slug || r.restaurantId}`)
  }

  const head = (c, i) => {
    const active = sort.key === c.key
    return (
      <Box component="th" key={c.key} scope="col" aria-sort={active ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}
        sx={{
          ...cellBase, position: 'sticky', top: 0, zIndex: i === 0 ? 4 : 3, bgcolor: '#F9FAFB', textAlign: c.align || 'right',
          fontSize: 12, fontWeight: 600, color: C.textMuted, ...(i === 0 ? { left: 0, minWidth: 200, boxShadow: `1px 0 0 ${C.border}` } : {})
        }}>
        <Box component="button" type="button" onClick={() => setSort(s => ({ key: c.key, dir: s.key === c.key ? -s.dir : c.text ? 1 : -1 }))}
          sx={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5, color: active ? C.text : C.textMuted, '&:focus-visible': { outline: `2px solid ${C.brandText}`, outlineOffset: 2, borderRadius: '2px' } }}>
          {c.label}
          <ArrowUpwardIcon sx={{ fontSize: 13, opacity: active ? 1 : 0.25, transform: active && sort.dir < 0 ? 'rotate(180deg)' : 'none' }} />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography component="h2" sx={cardTitleSx}>Restaurant leaderboard</Typography>
          <Typography sx={cardSubSx}>Dashboard orders in this range · click a restaurant to open its dashboard for the same dates</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField size="small" value={q} onChange={e => setQ(e.target.value)} placeholder="Search restaurants"
            inputProps={{ 'aria-label': 'Search restaurants' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.textMuted }} /></InputAdornment> }}
            sx={{ width: 220, '& .MuiOutlinedInput-root': { height: 36, borderRadius: '8px', fontSize: 14 } }} />
          <FormControlLabel control={<Switch size="small" checked={showZero} onChange={e => setShowZero(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: C.text }}>Show restaurants with 0 orders</Typography>} />
        </Box>
      </Box>

      <Box sx={{ mt: 2, maxHeight: 560, overflow: 'auto', border: `1px solid ${C.border}`, borderRadius: '10px', ...tabular }} tabIndex={0} aria-label="Restaurant leaderboard, scrollable">
        {loading && !data ? <Box sx={{ p: 2 }}>{[0, 1, 2, 3, 4].map(i => <Skeleton key={i} height={36} />)}</Box> : (
          <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5 }}>
            <Box component="thead"><Box component="tr">{COLS.map(head)}</Box></Box>
            <Box component="tbody">
              {rows.length === 0 ? (
                <Box component="tr"><Box component="td" colSpan={COLS.length} sx={{ ...cellBase, color: C.textMuted, py: 3, textAlign: 'center' }}>No restaurants match.</Box></Box>
              ) : rows.map(r => (
                <Box component="tr" key={r.restaurantId} tabIndex={0} role="link" aria-label={`Open ${r.name} dashboard`}
                  onClick={() => open(r)} onKeyDown={e => { if (e.key === 'Enter') open(r) }}
                  sx={{ cursor: 'pointer', '&:hover td': { bgcolor: '#F9FAFB' }, '&:focus-visible': { outline: `2px solid ${C.brandText}`, outlineOffset: -2 } }}>
                  {COLS.map((c, i) => (
                    <Box component="td" key={c.key} sx={{
                      ...cellBase, bgcolor: '#fff', textAlign: c.align || 'right', color: C.text,
                      ...(i === 0 ? { position: 'sticky', left: 0, zIndex: 1, fontWeight: 600, boxShadow: `1px 0 0 ${C.border}`, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' } : {})
                    }}>
                      {c.key === 'gmvDelta' ? <DeltaCell row={r} /> : c.bar ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <Box sx={{ width: 56, height: 6, borderRadius: 3, bgcolor: '#F1F2F4', overflow: 'hidden' }} aria-hidden>
                            <Box sx={{ width: `${Math.min(100, (r.share || 0) * 100)}%`, height: '100%', bgcolor: C.brand }} />
                          </Box>
                          <span>{$(r.grandTotal)}</span>
                        </Box>
                      ) : c.text ? (
                        <>{r.name}{r.isActive === false ? <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: C.textMuted }}>inactive</Box> : null}</>
                      ) : c.fmt(r[c.key])}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
            {t ? (
              <Box component="tfoot">
                <Box component="tr">
                  {COLS.map((c, i) => (
                    <Box component="td" key={c.key} sx={{
                      ...cellBase, position: 'sticky', bottom: 0, zIndex: i === 0 ? 3 : 2, bgcolor: '#F3F4F6', fontWeight: 700, color: C.text,
                      textAlign: c.align || 'right', borderTop: `1px solid ${C.borderStrong}`, borderBottom: 0,
                      ...(i === 0 ? { left: 0, boxShadow: `1px 0 0 ${C.border}` } : {})
                    }}>
                      {c.key === 'name' ? 'Total' : c.key === 'gmvDelta' ? <DeltaCell row={t} /> : c.fmt(t[c.key])}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: C.textMuted, mt: 1 }}>
        Totals cover every restaurant in the filter, including rows hidden by the search or the zero-orders toggle, and equal the cards above.
      </Typography>
    </Box>
  )
}
